import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Settings } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { esAdmin } from "@/lib/roles";
import { fechaHora } from "@/lib/format";
import {
  A_MANO_ADMIN, ANULAR_A_MANO, AVISO_PRE, CANAL, CODIGO_SES_ADMIN, GESTIONADA_CON_ANULACION, OPERACION,
  SIN_RESPUESTA_ADMIN, avisosDe, bSinCodigo, claseEstado, cuentaAtras, esFinal, estadoComunicacion,
  gestionadaConAnulacion, resumenComprobacion,
} from "@/lib/registro";
import CommunicationActions, { ListaCambios, MarcaPruebas } from "@/components/registry/CommunicationActions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const LISTA = "/admin/registry/communications";
const LIMITE = 200; // las que devuelve la API por defecto (las más recientes)
const FILTROS = [["attention", "Piden atención"], ["", "Todas"], ["mossos", "Mossos"], ["ses", "SES"]];
const SIN_RESPUESTA = ("Los Mossos no dan el resultado: revísala en el portal de comunicaciones de Canal Empresa y, "
  + "si consta, márcala como comunicada a mano.");

// Lo que falla, en castellano: lo que falta, lo que no valida, el rechazo, el error de envío y lo que hay que hacer a
// mano. `anulada` (por defecto, `reservation_cancelled` de la API): la alta de los Mossos que llegó sin admitirse se
// gestiona con su anulación a mano, que lleva la única instrucción (no dos filas con instrucciones distintas).
// Además, sin pintarlo como un fallo: en cola tras un intento sin respuesta, cuándo se reintenta (`last_error` de
// jobs.py); y en una modificación, qué ha cambiado (`changes`, en castellano y sin valores), que es lo que hay que
// comunicar en el formulario de Canal Empresa.
// Sin `admin` (el operador), las instrucciones de los botones que no tiene (marcar a mano, comprobar si llegó, añadir
// el código de SES) se cambian por quién lo resuelve.
export function Problemas({ c, anulada = Boolean(c?.reservation_cancelled), admin = true }) {
  if (gestionadaConAnulacion(c, anulada)) return <p className="mt-2 text-sm text-[#5C5C5C]">{GESTIONADA_CON_ANULACION}</p>;
  const anularEnMossos = c.channel === "mossos" && c.operation === "cancellation";
  const aMano = c.status === "manual_required" ? c.manual?.reason || (anularEnMossos ? ANULAR_A_MANO : null) : null;
  const sinRespuesta = c.status === "sent" && c.remote?.no_answer;
  const codigoSes = bSinCodigo(c); // su única falta es el código de SES, que añade el administrador
  const delAdministrador = admin ? null
    : (aMano && A_MANO_ADMIN) || (sinRespuesta && SIN_RESPUESTA_ADMIN) || (codigoSes && CODIGO_SES_ADMIN) || null;
  const lineas = [...(admin || !codigoSes ? c.missing || [] : []), ...(c.validation_errors || []),
    ...(c.rejection?.errors || []), c.rejection?.description, c.status === "error" ? c.last_error : null,
    admin ? aMano : null, admin && sinRespuesta ? SIN_RESPUESTA : null].filter(Boolean);
  const cola = c.status === "ready" ? c.last_error : "";
  const cambios = c.operation === "modification" && (c.changes || []).length > 0;
  if (lineas.length === 0 && !cola && !cambios && !delAdministrador) return null;
  return (
    <>
      {cola && <p className="mt-2 text-sm text-[#5C5C5C]" data-testid={`com-${c.id}-cola`}>{cola}</p>}
      {lineas.length > 0 && (
        <ul className="mt-2 text-sm text-[#8C3B26] list-disc pl-5">{lineas.map((t, i) => <li key={i}>{t}</li>)}</ul>
      )}
      {delAdministrador && <p className="mt-2 text-sm text-[#5C5C5C]" data-testid={`solo-admin-${c.id}`}>{delAdministrador}</p>}
      <ListaCambios c={c} />
    </>
  );
}

export default function Registry() {
  // El operador lee la lista; ejecutar el envío, los ajustes y las acciones de cada comunicación son del administrador.
  const admin = esAdmin(useAuth()?.user);
  const [filtro, setFiltro] = useState("attention");
  const [items, setItems] = useState(null);
  const [comprobando, setComprobando] = useState(false);
  const ultima = useRef(0);
  const cargar = useCallback(async () => {
    const params = filtro === "attention" ? { attention: 1 } : filtro ? { channel: filtro } : {};
    const n = ++ultima.current;
    try {
      const { data } = await api.get(LISTA, { params });
      if (n !== ultima.current) return; // la respuesta de un filtro anterior que llega tarde
      setItems(data);
    } catch (err) {
      if (n === ultima.current) toast.error(formatError(err));
    }
  }, [filtro]);
  useEffect(() => { cargar(); }, [cargar]);
  // El entorno de los Mossos (el resumen), y el nombre y el canal de cada oficina (los ajustes): si no llegan, la
  // página sigue sin el aviso ni los nombres. El aviso de PRE habla de Canal Empresa: solo si la empresa tiene algo de
  // los Mossos (una oficina en los ajustes o una comunicación en la lista).
  const [entorno, setEntorno] = useState(null);
  const [oficinas, setOficinas] = useState({});
  const [mossosEnAjustes, setMossosEnAjustes] = useState(false);
  useEffect(() => {
    let vivo = true;
    api.get("/admin/registry/summary")
      .then(({ data }) => { if (vivo) setEntorno(typeof data?.mossos_env === "string" ? data.mossos_env : null); })
      .catch(() => {});
    api.get("/admin/registry/settings")
      .then(({ data }) => {
        const ests = Array.isArray(data?.establishments) ? data.establishments : [];
        const canales = data?.channels && typeof data.channels === "object" ? Object.values(data.channels) : [];
        if (!vivo) return;
        setOficinas(Object.fromEntries(ests.filter((e) => e?.id && e.name).map((e) => [e.id, e.name])));
        setMossosEnAjustes(canales.includes("mossos"));
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);
  const comprobar = async () => {
    setComprobando(true);
    try {
      const { data } = await api.post("/admin/registry/run");
      toast.success(resumenComprobacion(data));
      await cargar();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setComprobando(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
          {admin && (
            <Link to="/admin/registro-viajeros/ajustes" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><Settings className="h-4 w-4 mr-2" /> Ajustes</Link>
          )}
        </div>
      </header>
      <main className="container-x py-8 space-y-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Registro de viajeros y alquileres</h1>
        {entorno === "PRE" && (mossosEnAjustes || (items || []).some((c) => c.channel === "mossos")) && (
          <div className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="reg-pre">
            {AVISO_PRE}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {FILTROS.map(([k, label]) => (
            <Button key={k || "todas"} variant={filtro === k ? "default" : "outline"} aria-pressed={filtro === k}
              onClick={() => setFiltro(k)} data-testid={`reg-filter-${k || "all"}`}>{label}</Button>
          ))}
          {admin && (
            <Button variant="ghost" onClick={comprobar} disabled={comprobando} data-testid="reg-run">
              <RefreshCw className={`h-4 w-4 mr-2 ${comprobando ? "animate-spin" : ""}`} /> Comprobar ahora
            </Button>
          )}
        </div>
        {items && items.length === 0 && <p className="text-[#5C5C5C]">{filtro === "attention" ? "Nada pendiente." : "No hay comunicaciones."}</p>}
        {items && items.length >= LIMITE && (
          <p className="text-sm text-[#5C5C5C]" data-testid="reg-limit">
            Se enseñan las {LIMITE} más recientes: las anteriores están en la ficha de cada reserva.
          </p>
        )}
        <div className="space-y-3">
          {(items || []).map((c) => {
            const plazo = !esFinal(c) && c.status !== "sent" ? cuentaAtras(c.due_at) : "";
            const motivo = c.status === "discarded" ? c.discarded_reason : c.status === "superseded" ? c.superseded_reason : "";
            const oficina = oficinas[c.establishment_id];
            return (
              <div key={c.id} className="bg-white border border-[#DCD8D0] rounded-xl p-4" data-testid={`reg-row-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <Link to={`/admin/reservations/${c.reservation_id}`} className="font-mono font-semibold text-marca underline">{c.reservation_code}</Link>
                    <span className="text-[#1A1A1A]">
                      {" "}· {OPERACION[c.operation] || c.operation} · {CANAL[c.channel] || c.channel}{oficina ? ` · ${oficina}` : ""}
                    </span>
                    <div className="text-xs text-[#5C5C5C]" data-testid={`reg-when-${c.id}`}>
                      {fechaHora(c.event_at)}{plazo ? ` · ${plazo}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <MarcaPruebas c={c} />
                    <Badge className={`${claseEstado(c)} border-0`}>{estadoComunicacion(c)}</Badge>
                  </div>
                </div>
                {motivo && <p className="mt-1 text-xs text-[#5C5C5C]">{motivo}</p>}
                <Problemas c={c} admin={admin} />
                {avisosDe(c, admin).length > 0 && <ul className="mt-1 text-xs text-[#9C6512]">{avisosDe(c, admin).map((w, i) => <li key={i}>{w}</li>)}</ul>}
                <div className="mt-3"><CommunicationActions c={c} onDone={cargar} admin={admin} /></div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
